import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import Search from "../../../assets/images/search.svg";
import "./UserManagement.scss";
import DataTable from "react-data-table-component";
import { getUsers, deleteUser } from "../../../services/userService";

function UserManagement() {
  const [searchTerm, setSearchTerm] = useState("");
  const [userToDelete, setUserToDelete] = useState(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: (userId) => deleteUser(userId),
    onSuccess: (data) => {
      toast.success(data?.message || "User deleted successfully", {
        position: "top-right",
        autoClose: 3000,
      });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      setUserToDelete(null);
    },
    onError: (err) => {
      const msg =
        err?.response?.data?.message || err?.message || "Failed to delete user";
      toast.error(msg, { position: "top-right", autoClose: 4000 });
    },
  });

  const handleDeleteClick = (row) => {
    setUserToDelete(row);
  };

  const handleConfirmDelete = () => {
    if (!userToDelete) return;
    deleteUserMutation.mutate(userToDelete.id);
  };

  const handleCloseDeleteModal = () => {
    if (!deleteUserMutation.isPending) setUserToDelete(null);
  };

  // Fetch users using React Query
  const {
    data: usersData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["users"],
    queryFn: getUsers,
    retry: 1,
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch users";
      toast.error(errorMessage);
    },
  });

  // Transform API data to match table structure
  const transformedData = useMemo(() => {
    if (!usersData) return [];

    // Handle API response structure: { status, count, data: [...] }
    const users = usersData.data || usersData.users || usersData || [];

    return users.map((user) => {
      // Format date if needed
      const formatDate = (dateString) => {
        if (!dateString) return { date: "N/A", time: "" };
        try {
          const date = new Date(dateString);
          const dateStr = date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });
          const timeStr = date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });
          return { date: dateStr, time: timeStr };
        } catch (e) {
          return { date: dateString, time: "" };
        }
      };

      const dateInfo = formatDate(user.createdAt);

      // Map kycVerification to kycStatus
      const kycStatusMap = {
        verified: "Verified",
        unverified: "Unverified",
        pending: "Pending",
      };
      const kycStatus =
        kycStatusMap[user.kycVerification?.toLowerCase()] ||
        user.kycVerification?.charAt(0).toUpperCase() +
          user.kycVerification?.slice(1) ||
        "Unverified";

      // Map status to accountStatus
      const accountStatusMap = {
        active: "Active",
        pending: "Pending",
        suspended: "Suspended",
        inactive: "Inactive",
      };
      const accountStatus =
        accountStatusMap[user.status?.toLowerCase()] ||
        user.status?.charAt(0).toUpperCase() + user.status?.slice(1) ||
        (user.active ? "Active" : "Inactive");

      // Determine user type - check if userType is "business" or if it's a business user
      const userType =
        user.userType === "businessUser" || user.userType === "Business User"
          ? "Business User"
          : "User";

      return {
        id: user._id || user.id,
        image:
          user.profilePictureUrl ||
          user.profileImage ||
          user.image ||
          process.env.PUBLIC_URL + "/images/user.svg",
        name:
          user.username ||
          user.name ||
          user.fullName ||
          `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
          user.email ||
          "N/A",
        userType: userType,
        email: user.email || "N/A",
        dateJoined: dateInfo.date,
        timeJoined: dateInfo.time,
        kycStatus: kycStatus,
        accountStatus: accountStatus,
        // Keep original user data for reference
        originalData: user,
      };
    });
  }, [usersData]);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return transformedData;

    const searchLower = searchTerm.toLowerCase();
    return transformedData.filter(
      (user) =>
        user.name.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower) ||
        user.userType.toLowerCase().includes(searchLower),
    );
  }, [transformedData, searchTerm]);

  const columns = [
    {
      name: "User name & Email",
      grow: 2,
      sortable: true,
      cell: (row) => (
        <div className="user-cell">
          <img
            src={row.image}
            alt="user"
            className="user-img"
            onError={(e) => {
              e.target.src = process.env.PUBLIC_URL + "/images/user.svg";
            }}
          />
          <div>
            <strong>{row.name}</strong>
            <br />
            <span>{row.email}</span>
          </div>
        </div>
      ),
    },
    {
      name: "User Type",
      selector: (row) => row.userType,
      sortable: true,
    },
    {
      name: "Date Joined",
      sortable: true,
      cell: (row) => (
        <div className="timestamp">
          <strong>{row.dateJoined}</strong>
          <br />
          <span>{row.timeJoined}</span>
        </div>
      ),
    },
    {
      name: "Verification (KYC)",
      sortable: true,
      cell: (row) => (
        <span className={`status-badge ${row.kycStatus.toLowerCase()}`}>
          <span className="dot"></span>
          {row.kycStatus}
        </span>
      ),
    },
    // {
    //   name: "Status",
    //   sortable: true,
    //   cell: (row) => (
    //     <span className={`status-badge ${row.accountStatus.toLowerCase()}`}>
    //       <span className="dot"></span>
    //       {row.accountStatus}
    //     </span>
    //   ),
    // },
    {
      name: "",
      button: true,
      cell: (row) => {
        const handleEdit = () => {
          const profilePath =
            row.userType === "Business User"
              ? "/user-management/business-profile"
              : "/user-management/user-profile";

          // Navigate with state containing user data
          navigate(profilePath, {
            state: {
              userData: row.originalData,
              userId: row.id,
            },
          });
        };

        return (
          <div className="actions">
            <button
              type="button"
              onClick={handleEdit}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
              }}
              aria-label="Edit user"
            >
              <img
                src={process.env.PUBLIC_URL + "/images/edit.svg"}
                className="me-2"
                alt="edit"
              />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(row);
              }}
              disabled={deleteUserMutation.isPending}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
              }}
              aria-label="Delete user"
            >
              <img
                src={process.env.PUBLIC_URL + "/images/trash.svg"}
                alt="trash"
              />
            </button>
          </div>
        );
      },
    },
  ];

  const customStyles = {
    table: {
      style: {
        backgroundColor: "transparent",
        color: "#0D0D0D",
      },
    },
    headRow: {
      style: {
        backgroundColor: "transparent",
        color: "#0D0D0D",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
      },
    },
    headCells: {
      style: {
        fontSize: "10px",
        fontWeight: "500",
        color: "#0D0D0D",
        padding: "12px 10px",
        backgroundColor: "transparent",
      },
    },
    rows: {
      style: {
        backgroundColor: "transparent",
        color: "#0D0D0D",
        fontSize: "10px",
        minHeight: "60px",
        "&:hover": {
          backgroundColor: "#EAE3E0 !important",
          border: "none !important",
          outline: "none !important",
        },
      },
    },
  };

  return (
    <React.Fragment>
      <header className="user-manage-panel-header items-center gap-3 flex content-justify-between">
        <div className="left-side">
          <h4 className="title">User Management</h4>
        </div>
        <div className="right-side">
          <div className="search_bar">
            <img src={Search} alt="Search" />
            <input
              type="text"
              placeholder="Search for user management..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </header>
      <div className="user-management">
        <div className="card">
          <h4>Users</h4>
          {isLoading ? (
            <div className="loading-container">
              <div className="loader"></div>
              <p>Loading users...</p>
            </div>
          ) : isError ? (
            <div className="error-container">
              <p>Error loading users. Please try again.</p>
              {error && (
                <small>
                  {error?.response?.data?.message || error?.message}
                </small>
              )}
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              highlightOnHover
              responsive
              selectableRows
              customStyles={customStyles}
              noDataComponent="No users found"
            />
          )}
        </div>
      </div>

      <Modal
        show={!!userToDelete}
        onHide={handleCloseDeleteModal}
        centered
        backdrop="static"
        className="delete-confirm-modal"
        aria-labelledby="deleteUserModalLabel"
      >
        <Modal.Header>
          <Modal.Title id="deleteUserModalLabel">Delete user</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="delete-confirm-message">
            Are you sure you want to delete{" "}
            <strong>
              {userToDelete
                ? userToDelete.name || userToDelete.email || "this user"
                : ""}
            </strong>
            ? This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="close-btn"
            onClick={handleCloseDeleteModal}
            disabled={deleteUserMutation.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="red-btn"
            onClick={handleConfirmDelete}
            disabled={deleteUserMutation.isPending}
          >
            {deleteUserMutation.isPending ? "Deleting..." : "Delete"}
          </button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
}

export default UserManagement;
